import { textOr } from "utils/helpers"
import { defaults } from "lodash"
import MessagesType from "models/messages-type"
import MdiIconName from "admin/components/mdi-icon-name"
import { BootstrapColor } from "admin/components/types"
import { ReportStats } from "models/reports-model"

export interface ActivityScreen extends LessonScreenCommon {
  secondsToComplete: number
  points: number
  answerExplanation: string
}

export interface ScreenWithQuestion {
  question: string
}

export interface ScreenWithAudio {
  questionAudioUrl?: string
}

export interface ScreenWithYoutube {
  youtubeUrl: string
  youtubeStart?: number
  youtubeEnd?: number
}

export interface ScreenWithQuestionImage extends LessonScreenCommon {
  questionImageUrl?: string
  questionImageWidth: number
  questionImageHeight: number
  questionDesktopImageUrl?: string
}

export interface AudioResponse {
  questionAudioUrl?: string
}

export type BroadcastMessageType =
  | "ReconnectFailed"
  | "ReconnectSuccessful"
  | "ReconnectTeacherSuccessful"
  | "SessionStarted"
  | "UsernameTaken"
  | "StudentAdded"
  | "StudentRemoved"
  | "Start"
  | "ScreenFinished"
  | "StudentAnswer"
  | "NewReviewScreen"
  | "TimerToggle"
  | "TimerTimedOut"

export default interface BroadcastMessage {
  type: BroadcastMessageType
  timestamp: Date
  data?: any
}

export interface Campaign {
  id: string
  name: string
  timeCreated: Date
  timeModified: Date
}

export interface CampaignParticipant {
  id: string
  campaignId: string
  userId: string
  monSchoolId: number | undefined
  timeCreated: Date
}

export interface ConnectPair {
  id: string
  screenId: string
  /** place among its siblings, fixed at creation; see creationOrder in utils/helpers */
  ordinal: number

  leftImageUrl: string
  leftImageWidth?: number
  leftImageHeight?: number
  leftText: string
  leftDesktopImageUrl?: string

  rightImageUrl: string
  rightImageWidth?: number
  rightImageHeight?: number
  rightText: string
  rightDesktopImageUrl?: string
}

export interface ConnectScreen extends ActivityScreen {
  type: ScreenType.Connect
  question?: string
  pairs: ConnectPair[]
}

export function isLinkCorrect(data: {
  leftPair?: ConnectPair
  rightPair?: ConnectPair
}) {
  const { leftPair, rightPair } = data

  if (leftPair === undefined || rightPair === undefined) {
    return false
  }

  let isCorrect = false
  // first, attempt the quick path
  if (leftPair.id === rightPair.id) {
    isCorrect = true
  } else {
    let correctContentForRight = textOr(
      leftPair.rightImageUrl,
      leftPair.rightText || ""
    )
    let correctContentForLeft = textOr(
      rightPair.leftImageUrl,
      rightPair.leftText || ""
    )
    let actualLeft = textOr(leftPair.leftImageUrl, leftPair.leftText || "")
    let actualRight = textOr(rightPair.rightImageUrl, rightPair.rightText || "")

    isCorrect =
      actualLeft == correctContentForLeft ||
      actualRight == correctContentForRight
  }

  return isCorrect
}

export interface DragDropAnswer {
  id: string
  categoryId: string
  /** place among its siblings, fixed at creation; see creationOrder in utils/helpers */
  ordinal: number
  imageUrl: string
  imageWidth: number
  imageHeight: number
  desktopImageUrl: string
  text?: string
}

export interface DragDropCategory {
  id: string
  screenId: string
  /** place among its siblings, fixed at creation; see creationOrder in utils/helpers */
  ordinal: number
  title: string
  imageUrl: string
  imageWidth: number
  imageHeight: number
  desktopImageUrl: string
  answers: DragDropAnswer[]
}

export interface DragDropScreen extends ActivityScreen {
  type: ScreenType.DragDrop
  categories: DragDropCategory[]
}

export interface DrawScreen extends ActivityScreen, ScreenWithQuestionImage {
  type: ScreenType.Drawing
  question: string
  questionAudioUrl?: string
}

export enum ImageType {
  ConnectPair,
  DragDropCategory,
  DragDropCategoryAnswer,
  DrawScreen,
  KeyboardScreen,
  Lesson,
  MapScreen,
  PresentationScreen,
  QuizAnswer,
  QuizScreen,
  ProfilePicture,
  PollScreen,
  PollAnswer,
  OpenText,
  Ocr,
  Logo,
}

export function imageSizeByType(type: ImageType): { w: number; h: number } {
  switch (type) {
    case ImageType.ConnectPair:
      return { w: 300, h: 200 }
    case ImageType.DragDropCategory:
      return { w: 764, h: 430 }
    case ImageType.DragDropCategoryAnswer:
      return { w: 200, h: 200 }
    case ImageType.DrawScreen:
      return { w: 764, h: 430 }
    case ImageType.KeyboardScreen:
      return { w: 764, h: 430 }
    case ImageType.Lesson:
      return { w: 300, h: 300 }
    case ImageType.MapScreen:
      return { w: 764, h: 430 }
    case ImageType.PresentationScreen:
      return { w: 764, h: 430 }
    case ImageType.QuizAnswer:
      return { w: 300, h: 300 }
    case ImageType.QuizScreen:
      return { w: 600, h: 600 }
    case ImageType.ProfilePicture:
      return { w: 128, h: 128 }
    case ImageType.PollScreen:
      return { w: 600, h: 600 }
    case ImageType.PollAnswer:
      return { w: 300, h: 300 }
    case ImageType.OpenText:
      return { w: 764, h: 430 }
    default:
      return { w: 0, h: 0 }
  }
}

export interface KeyboardScreen
  extends ActivityScreen,
    ScreenWithQuestionImage {
  type: ScreenType.Keyboard
  alphabet: string
  question: string
  isAnswerHidden?: boolean
  answers: KeyboardAnswer[]
}

export interface KeyboardAnswer {
  isExact: boolean
  content: string
}

export function keyboardAnswerKey(answer: KeyboardAnswer): string {
  let key: string = "kb_"
  if (answer.isExact) key += "exact_"
  else key += "containing_"
  key += answer.content
  return key
}

export enum LessonOrigin {
  Community = 0,
  Nimero = 1,
  Prosveta = 2,
}

export enum LifecycleState {
  NeverPublished = 0,
  Draft = 1,
  Published = 2,
}

export interface LessonStandard {
  lessonId: string
  standardId: string
  isApproved: boolean
}

export enum LessonVisibility {
  Public,
  Mine,
  Organization,
}

export interface LessonSearchInfo {
  id: string
  name: string
  description: string
  lessonAuthorId: string
  lessonAuthorName: string
  lessonAuthorPicture: string | null
  originalLessonAuthorName: string | null
  originalLessonAuthorPicture: string | null
  originalAuthorId?: string | undefined | null
  originalLessonId?: string | undefined | null
  countScreens: number
  countTimesPlayedByStudents: number
  grades: string[]
  subjects: string[]
  origin: LessonOrigin
  lastModified: Date
  timeCreated: Date
  timePublished?: Date
  state: LifecycleState
  downloadUrl?: string
  imageUrl: string
  imageWidth: number
  imageHeight: number
  desktopImageUrl: string
  isPublic: boolean
  lessonStandards: LessonStandard[]
  languageCode?: string
  themeId?: number | null
}

export type LessonScreenCommon = {
  id: string
  lessonId: string
  ordinal: number
}

/*  Presentation = 0,
  Connect = 1,
  DragDrop = 2,
  Drawing = 3,
  Keyboard = 5,
  Map = 6,
  Quiz = 7*/

export type LessonScreen =
  | PresentationScreen
  | DragDropScreen
  | DrawScreen
  | KeyboardScreen
  | MapScreen
  | QuizScreen
  | ConnectScreen
  | PollScreen
  | OpenTextScreen

export function screenQuestion(screen: LessonScreen): string | null {
  let questionHtml: string | null = null

  if (screen.type === ScreenType.Connect) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.DragDrop) {
    questionHtml = ""
  } else if (screen.type === ScreenType.Drawing) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.Keyboard) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.Presentation) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.Map) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.Quiz) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.Poll) {
    questionHtml = screen.question || ""
  } else if (screen.type === ScreenType.OpenText) {
    questionHtml = screen.question || ""
  }

  return questionHtml
}

export function deepCloneScreens(screens: LessonScreen[]): LessonScreen[] {
  return screens.map((screen) => {
    const clone: LessonScreen = { ...screen }

    if (clone.type === ScreenType.Connect) {
      clone.pairs = clone.pairs.map((x) => ({ ...x }))
    } else if (clone.type === ScreenType.DragDrop) {
      clone.categories = clone.categories.map((cat) => {
        const c: DragDropCategory = { ...cat }
        c.answers = c.answers.map((a) => ({ ...a }))
        return c
      })
    } else if (clone.type === ScreenType.Keyboard) {
      clone.answers = clone.answers.map((x) => ({ ...x }))
    } else if (clone.type === ScreenType.Map) {
      clone.ellipseAnswers = clone.ellipseAnswers.map((x) => ({ ...x }))
    } else if (clone.type === ScreenType.Quiz) {
      clone.answers = clone.answers.map((x) => ({ ...x }))
    } else if (clone.type === ScreenType.Poll) {
      clone.answers = clone.answers.map((x) => ({ ...x }))
    }

    return clone
  })
}

export type LessonIndex = LessonSearchInfo

export interface Lesson extends LessonSearchInfo {
  screens: LessonScreen[]
  averageSuccessRate: number
  timesPlayed: number
  standards: Standard[]
  unsplashImages: UnsplashImage[]
}

export type UnsplashImage = {
  unsplashId: string
  description?: string
  rawUrl: string
  downloadUrl: string
  authorName: string
  authorPage: string
}

export interface LessonWithValidation extends Lesson {
  validationMessages: ValidationMessage[]
}

export interface ValidationMessage {
  id: string
  message: string
  for?: string
}

export interface ValidatedResource<T> {
  resource: T
  validationMessages: ValidationMessage[]
}

export interface ValidationHash {
  [id: string]: ValidationMessage[]
}

export function lessonGradeText(lesson: Lesson | null): string {
  if (!lesson) return ""

  switch (lesson.grades.length) {
    case 0:
      return ""
    case 1:
      return lesson.grades[0]
    default:
      return `${lesson.grades[0]} - ${lesson.grades[lesson.grades.length - 1]}`
  }
}

export function newEmptyLesson(template: Partial<Lesson>): Lesson {
  var now = new Date()

  return defaults(template as any, {
    name: "",
    lessonAuthorId: "",
    grades: [],
    subjects: [],
    origin: LessonOrigin.Community,
    lastModified: now,
    timeCreated: now,
    state: LifecycleState.NeverPublished,
    downloadUrl: "",
    imageUrl: "",
    imageWidth: 300,
    imageHeight: 300,
    desktopImageUrl: "",
    isPublic: false,
    screens: [],
    standards: [],
  })
}

export enum LicenseProductType {
  Envision = 1,
  Jumpido = 2,
  EnvisionPlay = 4,
}

export interface License {
  lastExpireDate: Date
  firstExpireDate: Date
  allowedClasses: number
  hasNVO2021?: boolean
  hasPlayLicense?: boolean
  products: LicenseProductType[]
}

export type KnownOrderPackageTypes = "SingleMonthly" | "SingleYearly"

export enum LogEntryProduct {
  EnvisionPlay,
  EnvisionEditor,
  EnvisionDesktop,
}

export interface LogEntry {
  product: LogEntryProduct
  version: string
  module?: string
  location: string
  message: string
}

export interface MapEllipseAnswer {
  id: string
  mapId?: string
  /** place among its siblings, fixed at creation; see creationOrder in utils/helpers */
  ordinal: number
  width: number
  height: number
  x: number
  y: number
}

export interface MapScreen extends ActivityScreen, ScreenWithQuestionImage {
  type: ScreenType.Map
  question: string
  questionAudioUrl?: string
  ellipseAnswers: MapEllipseAnswer[]
}

export interface OpenTextScreen
  extends ActivityScreen,
    ScreenWithQuestionImage {
  type: ScreenType.OpenText
  question: string
}

export enum PaymentType {
  NVO_Student = 1,
  NVO_Class = 2,
  Classbuddy_Year = 10,
  Classbuddy_Month = 20,
  Classbuddy_EndofSchoolYear = 30,
  Classbuddy_Package5 = 40,
}

export type PlaySessionCertificateCommon = {
  reportId: string
  title: string
  schoolName: string
  studentSuccessRate: number
  teacherName: string
  teacherId: string
  userId: string
  studentName: string
  downloadUrl: string
}

export type PlaySessionCertificate = PlaySessionCertificateCommon & {
  date: Date
}

export type PlaySessionCertificateResponse = PlaySessionCertificateCommon & {
  date: string
}

export interface PlaySessionWithStats {
  session: PlaySession
  className: string
  totalPlayers: number
  successRate: number
}

export interface PlaySession {
  id: string
  title: string
  timeStarted: Date
  studentCanPlayMoreThanOnce: boolean
  teacherId: string
  isActive: boolean
  endDate?: Date
  lessonId?: string
  shuffleScreens?: boolean
  shuffleAnswers?: boolean
  grades: string[]
  subjects: string[]
  classId?: string
  code: string
  reportId?: string
  //studentSessions: StudentPlaySession[]
  disableMemes: boolean | null
  certificateMinSuccessRate: number | null | undefined
  enableTimer: boolean | null | undefined
  enableGraphicalLogin: boolean | null | undefined
  enableNewUserRegistration: boolean | null | undefined
}

export interface PollAnswer {
  id: string
  screenId: string
  text?: string
  url?: string
  imageWidth: number
  imageHeight: number
  desktopUrl?: string
  ordinal: number
}

export interface PollScreen
  extends ActivityScreen,
    ScreenWithQuestionImage,
    ScreenWithQuestion {
  type: ScreenType.Poll
  question: string
  questionImageUrl?: string
  questionImageWidth: number
  questionImageHeight: number
  questionDesktopImageUrl?: string
  answers: PollAnswer[]
}

export interface PresentationScreen
  extends LessonScreenCommon,
    ScreenWithQuestionImage {
  type: ScreenType.Presentation
  question: string
  questionAudioUrl?: string
  youtubeUrl: string
  youtubeStart?: number
  youtubeEnd?: number
}

export interface PreviewKey {
  userId: string
  lessonId: string
}

export interface PreviewScreenChangedRequest {
  key: PreviewKey
  screen: LessonScreen
}

export interface LatestPreviewScreen {
  screen: LessonScreen
  timestamp: Date
  theme: Theme | null
}

export interface LatestPreviewScreenRaw {
  screen: LessonScreen
  timestamp: string
  theme?: Theme | null
}

export interface PreviewScreenResponse {
  latest?: LatestPreviewScreen
  isBusinessVersion: boolean
}

export interface PreviewScreenResponseRaw {
  latest?: LatestPreviewScreenRaw
  isBusinessVersion: boolean
}

export interface QuizAnswer {
  id?: string
  quizId?: string
  text: string
  isCorrect: boolean
  url?: string
  desktopUrl?: string
  ordinal: number
  imageWidth?: number
  imageHeight?: number
}

export enum QuizAnswersLayouts {
  Text = 0,
  Image = 1,
}

export enum QuizLayouts {
  SmallImage = 0,
  BigImage = 1,
}

export interface QuizScreen extends ActivityScreen, ScreenWithQuestionImage {
  type: ScreenType.Quiz
  answerWithGestures?: boolean
  answersLayout: QuizAnswersLayouts
  layout: QuizLayouts
  question: string
  questionAudioUrl?: string
  answers: QuizAnswer[]
}

export interface ReportIndex {
  id: string
  schoolId: string
  schoolName: string
  teacherId: string
  teacherName: string
  lessonId: string
  lessonName: string
  activeStudents: number
  date: string
  totalTime: string
  classId: string
  className: string
  successRate: number
  isDesktop: boolean
}

export interface ReportIndexForWeb extends ReportIndex {
  code?: string
  isActive: boolean
  endDate: string
}

export interface ReportWithPlaySessionAndStats {
  session?: PlaySession
  report: Report
  stats: ReportStats
}

export interface Report extends ReportIndex {
  screens: LessonScreen[]
  playedScreens: ReportedScreen[]
}

export function isReportCodeSingleSeatMode(code: string): boolean {
  return /SSS[a-z0-9]{32}/.test(code)
}

export interface ReportedAnswerCommon {
  id: string
  screenId: string
  userId: string
  studentName: string
  isCorrect: boolean
  isCompleted: boolean
  timeToComplete: string
  type: ScreenType
  points: number
}

export type ReportedAnswer =
  | ReportedConnectAnswer
  | ReportedDragDropAnswer
  | ReportedKeyboardAnswer
  | ReportedMapAnswer
  | ReportedQuizAnswer
  | ReportedDrawingAnswer
  | ReportedPollAnswer
  | ReportedOpenTextAnswer

export interface ReportedConnectAnswer extends ReportedAnswerCommon {
  //assignedPairIndex?: number
  //answeredPairIndex?: number
  // Note: for backwards compatibility, this may be NULL/not returned
  type: ScreenType.Connect
  links?: ReportedConnectLink[]
}

export interface ReportedConnectLink {
  id: string
  answerId: string
  leftPairId: string
  rightPairId: string
}

export interface ReportedDragDropAnswer extends ReportedAnswerCommon {
  type: ScreenType.DragDrop
  moves: ReportedDragDropMove[]
}

export interface ReportedDragDropMove {
  id: string
  answerId: string
  fromCategoryIndex: number
  toCategoryIndex: number
  itemCategoryIndex: number
  isCorrect: boolean
  originalAnswerId: string
}

export interface ReportedDrawingAnswer extends ReportedAnswerCommon {
  type: ScreenType.Drawing
  drawingCommands: ReportedDrawingCommand[]
}

export type ReportedDrawingCommand = {
  /** HTML Color */
  color: string
  /** Stroke width */
  stroke: number
  /** Points that need to be connected sequentially with the given color and stroke with
   */
  points: ReportedDrawingPoint[]
}

export type ReportedDrawingPoint = {
  x: number
  y: number
}

export interface ReportedKeyboardAnswer extends ReportedAnswerCommon {
  type: ScreenType.Keyboard
  answerText: string
}

export interface ReportedMapAnswer extends ReportedAnswerCommon {
  type: ScreenType.Map
  x: number
  y: number
}

export interface ReportedOpenTextAnswer extends ReportedAnswerCommon {
  type: ScreenType.OpenText
  answerText: string
}

export interface ReportedPollAnswer extends ReportedAnswerCommon {
  type: ScreenType.Poll
  answerIndexes: number[]
}

export interface ReportedQuizAnswer extends ReportedAnswerCommon {
  type: ScreenType.Quiz
  answerIndexes: number[]
}

export interface ReportedScreenCommon {
  id: string
  reportId: string
  screenIndex: number
  originalScreenId?: string
  timeStarted: string
  timeToComplete: string
  connectShuffledLeftIndices: number[]
  connectShuffledRightIndices: number[]
}

export type ReportedConnectScreen = ReportedScreenCommon & {
  type: ScreenType.Connect
  answers: ReportedConnectAnswer[]
}
export type ReportedKeyboardScreen = ReportedScreenCommon & {
  type: ScreenType.Keyboard
  answers: ReportedKeyboardAnswer[]
}
export type ReportedMapScreen = ReportedScreenCommon & {
  type: ScreenType.Map
  answers: ReportedMapAnswer[]
}
export type ReportedDragDropScreen = ReportedScreenCommon & {
  type: ScreenType.DragDrop
  answers: ReportedDragDropAnswer[]
}
export type ReportedQuizScreen = ReportedScreenCommon & {
  type: ScreenType.Quiz
  answers: ReportedQuizAnswer[]
}
export type ReportedDrawingScreen = ReportedScreenCommon & {
  type: ScreenType.Drawing
  answers: ReportedDrawingAnswer[]
}
export type ReportedPollScreen = ReportedScreenCommon & {
  type: ScreenType.Poll
  answers: ReportedPollAnswer[]
}
export type ReportedOpenTextScreen = ReportedScreenCommon & {
  type: ScreenType.OpenText
  answers: ReportedOpenTextAnswer[]
}

export type ReportedScreen =
  | ReportedConnectScreen
  | ReportedKeyboardScreen
  | ReportedMapScreen
  | ReportedDragDropScreen
  | ReportedQuizScreen
  | ReportedDrawingScreen
  | ReportedPollScreen
  | ReportedOpenTextScreen

//export interface SchoolClass {
//  id: string
//  name: string
//  grade: string
//  schoolId: string
//  registraionCode: string
//  maxStudents?: number
//  countStudents: number
//}

export interface SchoolClassSummary {
  id: string
  name: string
  countStudents: number
}

//export interface School {
//  id: string
//  name: string
//  country: string
//  city: string
//  classes: SchoolClass[]
//  teachers: User[]
//}

export enum ScreenType {
  Presentation = 0,
  Connect = 1,
  DragDrop = 2,
  Drawing = 3,
  // 4 was Individual, a legacy type that no longer exists
  Keyboard = 5,
  Map = 6,
  Quiz = 7,
  Poll = 8,
  OpenText = 9,
}

interface LessonScreenInfoInternal {
  type: ScreenType
  title: keyof MessagesType
  description: keyof MessagesType
  icon: MdiIconName
  color: BootstrapColor
}

export interface LessonScreenInfo {
  type: ScreenType
  title: string
  description: string
  icon: MdiIconName
  color: BootstrapColor
}

const screensInfoMap: Map<ScreenType, LessonScreenInfoInternal> = new Map([
  [
    ScreenType.Presentation,
    {
      type: ScreenType.Presentation,
      title: "screenPresentationTitle",
      description: "screenPresentationDescription",
      color: "info",
      icon: "mdi-presentation",
    },
  ],
  [
    ScreenType.Quiz,
    {
      type: ScreenType.Quiz,
      title: "screenQuizTitle",
      description: "screenQuizDescription",
      icon: "mdi-ab-testing",
      color: "primary",
    },
  ],
  [
    ScreenType.Map,
    {
      type: ScreenType.Map,
      title: "screenMapTitle",
      description: "screenMapDescription",
      icon: "mdi-map-search",
      color: "primary",
    },
  ],
  [
    ScreenType.Keyboard,
    {
      type: ScreenType.Keyboard,
      title: "screenKeyboardTitle",
      description: "screenKeyboardDescription",
      icon: "mdi-message-text",
      color: "primary",
    },
  ],
  [
    ScreenType.Connect,
    {
      type: ScreenType.Connect,
      title: "screenConnectTitle",
      description: "screenConnectDescription",
      icon: "mdi-connection",
      color: "primary",
    },
  ],
  [
    ScreenType.DragDrop,
    {
      type: ScreenType.DragDrop,
      title: "screenDragDropTitle",
      description: "screenDragDropDescription",
      icon: "mdi-puzzle",
      color: "primary",
    },
  ],
  [
    ScreenType.Drawing,
    {
      type: ScreenType.Drawing,
      title: "screenDrawTitle",
      description: "screenDrawDescription",
      icon: "mdi-palette",
      color: "warning",
    },
  ],
  [
    ScreenType.Poll,
    {
      type: ScreenType.Poll,
      title: "screenPollTitle",
      description: "screenPollDescription",
      icon: "mdi-poll",
      color: "warning",
    },
  ],
  [
    ScreenType.OpenText,
    {
      type: ScreenType.OpenText,
      title: "screenOpenTextTitle",
      description: "screenOpenTextDescription",
      icon: "mdi-text-search",
      color: "warning",
    },
  ],
])

export function screenTypeToString(type: ScreenType) {
  return ScreenType[type]
}

export function screenIconName(type: ScreenType): MdiIconName | null {
  const info = screensInfoMap.get(type)
  return info?.icon || null
}

export function screenInfo(
  type: ScreenType,
  msg: MessagesType
): LessonScreenInfo {
  const info = screensInfoMap.get(type)
  if (!info) throw "unknonw screen type " + type

  return {
    ...info,
    title: msg[info.title]!,
    description: msg[info.description]!,
  }
}

export interface Standard {
  id: string
  grade: string
  subject: string
  category: string
  name: string
  description: string
}

export interface User {
  id: string
  name: string
  email: string
  schoolName: string
  city: string
  phone: string
  avatarUrl: string
  lessonsCount: number
  schoolId: string
  parentId?: string
  grade?: string
  monSchoolId?: string
}

export enum Variant {
  School,
  Business,
}

export function mergeValidationMessage(
  forScreenId: string,
  current: ValidationHash,
  change: ValidationMessage[]
): ValidationHash {
  if (
    typeof change !== "undefined" &&
    change !== null &&
    Array.isArray(change)
  ) {
    let updated = { ...current }

    // first, remove lists with matching ids
    change.forEach((msg) => {
      updated[msg.id] = []
    })

    let countForScreen = 0
    // second, add new messages from server
    for (let msg of change) {
      const list = updated[msg.id]
      if (msg.id === forScreenId) {
        countForScreen++
      }
      list.push(msg)
    }

    if (countForScreen === 0) {
      delete updated[forScreenId]
    }

    return updated
  } else {
    return current
  }
}

export type Theme = {
  id: number
  backgroundUrl: string
}
